import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, ClipboardCheck, Clock, ArrowRight, Truck, Plus, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STAGE_LABEL = {
  machine_shop:     "Machine Shop",
  impregnation:     "Impregnation",
  powder_coating:   "Powder Coating",
  semi_finished:    "Semi Finished",
  assembly:         "Assembly",
  testing:          "Testing",
  final_inspection: "Final Inspection",
  finished:         "Finished Goods",
  rework:           "Rework",
  rejected:         "Rejected Warehouse",
};

export default function QualityInspectionsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);          // Phase 25 pending inspection records
  const [ginPending, setGinPending] = useState([]);    // GINs awaiting incoming-material inspection
  const [items, setItems] = useState([]);
  const [jobsMap, setJobsMap] = useState({});          // {job_id: job}
  const [dashboard, setDashboard] = useState(null);
  const [historyLog, setHistoryLog] = useState([]);
  const [stageFilter, setStageFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  // Completion dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [passedQty, setPassedQty] = useState(0);
  const [failedQty, setFailedQty] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [inspector, setInspector] = useState("");
  const [checkpoints, setCheckpoints] = useState([]);

  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const canDecide = ["admin", "quality", "quality_inspector"].includes(user?.role);

  const load = async () => {
    const [p, i, jobs, d, log, gins] = await Promise.all([
      api.get("/quality/inspections/pending").catch(() => ({ data: [] })),
      api.get("/items"),
      api.get("/production-jobs"),
      api.get("/quality/dashboard").catch(() => ({ data: null })),
      api.get("/qc-inspections?days=30").catch(() => ({ data: [] })),
      api.get("/gins").catch(() => ({ data: [] })),
    ]);
    setPending(p.data || []);
    setItems(i.data || []);
    setJobsMap(Object.fromEntries((jobs.data || []).map((j) => [j.id, j])));
    setDashboard(d.data);
    setHistoryLog((log.data || []).filter((r) => r.status !== "pending"));  // exclude pending from log
    setGinPending((gins.data || []).filter((g) => g.status === "pending_inspection"));
  };
  useEffect(() => { load(); }, []);

  const filteredPending = stageFilter === "all" ? pending : pending.filter((p) => p.stage === stageFilter);
  const filteredLog = stageFilter === "all" ? historyLog : historyLog.filter((r) => r.stage === stageFilter);

  const openInspection = (rec) => {
    setActive(rec);
    setPassedQty(rec.pending_qty || 0);
    setFailedQty(0);
    setRemarks("");
    setInspector(user?.name || user?.email || "");
    setCheckpoints([]);
    setDialogOpen(true);
  };

  const isTesting = active?.stage === "testing";
  const addCheckpoint = () => setCheckpoints((cs) => [...cs, { name: "", expected: "", actual: "", passed: true, notes: "" }]);
  const setCheckpoint = (idx, patch) => setCheckpoints((cs) => cs.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  const removeCheckpoint = (idx) => setCheckpoints((cs) => cs.filter((_, i) => i !== idx));

  const submitInspection = async () => {
    if (!inspector) return toast.error("Inspector name is required");
    const p = Number(passedQty || 0);
    const f = Number(failedQty || 0);
    if (p < 0 || f < 0) return toast.error("Quantities must be non-negative");
    if (p + f <= 0) return toast.error("Enter at least one of Passed / Failed quantity");
    if (p + f > (active.pending_qty || 0) + 1e-6) {
      return toast.error(`Passed + Failed (${p + f}) exceeds pending qty (${active.pending_qty})`);
    }
    if (isTesting && checkpoints.some((c) => !c.name.trim())) {
      return toast.error("Every checkpoint needs a name — remove blank rows before submitting");
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/quality/inspections/${active.id}/complete`, {
        passed_qty: p,
        failed_qty: f,
        remarks,
        inspector,
        checkpoints: isTesting ? checkpoints : [],
      });
      const routedToRejected = (data?.children || []).some((c) => c.current_stage === "rejected");
      const failDest = routedToRejected ? "Rejected Warehouse" : "Rework";
      toast.success(
        f === 0
          ? `${active.job_code} · ${p} passed → ${STAGE_LABEL[active.next_stage] || active.next_stage}`
          : p === 0
            ? `${active.job_code} · ${f} failed → ${failDest}`
            : `${active.job_code} · ${p} passed → ${STAGE_LABEL[active.next_stage]} · ${f} failed → ${failDest}`
      );
      if (data?.ncr) {
        toast.warning(`NC ${data.ncr.ncr_no} auto-raised — closure due ${new Date(data.ncr.target_date).toLocaleDateString()}`);
      }
      if (data?.rework_order) {
        toast.warning(`Rework Order ${data.rework_order.order_no} raised — rework & re-test by ${new Date(data.rework_order.due_date).toLocaleDateString()} (${data.rework_order.sla_working_days} working days)`);
      }
      if (routedToRejected) {
        toast.error(`${active.job_code} failed re-test — routed to the Rejected Warehouse`);
      }
      if (data?.indent) {
        toast.info(`Indent ${data.indent.code} raised · Semi Finished → Assembly`);
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Inspection submit failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="quality-inspections-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Quality</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
          Quality Inspections
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Every production job requires a QC inspection before it can move to the next stage.
          Fill Passed / Failed quantities below — passed qty auto-routes to the next stage, failed qty goes to Rework.
        </p>
      </div>

      {/* Dashboard summary */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 text-xs">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="qc-total-pending">
            <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Stage Inspections Pending</div>
            <div className="text-2xl mono font-extrabold text-amber-800 mt-0.5">{pending.length}</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3" data-testid="qc-gin-pending">
            <div className="text-[10px] uppercase tracking-wider text-blue-700 font-bold">Incoming (GIN) Pending</div>
            <div className="text-2xl mono font-extrabold text-blue-800 mt-0.5">{ginPending.length}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="qc-30d-approved">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Passed (last 30d)</div>
            <div className="text-2xl mono font-extrabold text-emerald-800 mt-0.5">{dashboard.last_30d?.approved ?? 0}</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-md p-3" data-testid="qc-30d-rejected">
            <div className="text-[10px] uppercase tracking-wider text-red-700 font-bold">Rejected (last 30d)</div>
            <div className="text-2xl mono font-extrabold text-red-800 mt-0.5">{dashboard.last_30d?.rejected ?? 0}</div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-md p-3" data-testid="qc-30d-rework">
            <div className="text-[10px] uppercase tracking-wider text-orange-700 font-bold">Rework (last 30d)</div>
            <div className="text-2xl mono font-extrabold text-orange-800 mt-0.5">{dashboard.last_30d?.rework ?? 0}</div>
          </div>
        </div>
      )}

      {/* Stage filter chips */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="qc-stage-filter">
        <span className="text-xs uppercase tracking-[0.14em] text-slate-500 mr-2">Filter by Stage:</span>
        {["all", ...Object.keys(STAGE_LABEL)].map((k) => (
          <button
            key={k}
            onClick={() => setStageFilter(k)}
            data-testid={`stage-chip-${k}`}
            className={`text-[11px] px-3 py-1 rounded-full border transition ${stageFilter === k ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"}`}
          >
            {k === "all" ? "All" : STAGE_LABEL[k]}
          </button>
        ))}
      </div>

      {/* Pending Inspection Queue — PRIMARY section at top */}
      <div className="bg-white border border-amber-200 rounded-lg overflow-hidden" data-testid="pending-inspection-queue">
        <div className="px-4 py-3 border-b border-amber-100 text-sm font-semibold text-amber-800 flex items-center justify-between bg-amber-50">
          <span><Clock className="inline w-4 h-4 mr-1.5" /> Pending Inspection Queue</span>
          <span className="mono text-xs text-amber-700">{filteredPending.length}</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
              <th className="py-2.5 px-4">Job</th>
              <th className="py-2.5 px-4">Current Stage</th>
              <th className="py-2.5 px-4">Item</th>
              <th className="py-2.5 px-4 text-right">Pending Qty</th>
              <th className="py-2.5 px-4">Next Stage</th>
              <th className="py-2.5 px-4">Requested</th>
              <th className="py-2.5 px-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredPending.length === 0 && (
              <tr>
                <td colSpan={7} className="py-10 text-center text-slate-500 text-sm">
                  <ShieldCheck className="inline w-4 h-4 mr-1 text-emerald-500" /> Queue clear — no pending inspections.
                </td>
              </tr>
            )}
            {filteredPending.map((rec) => {
              const job = jobsMap[rec.job_id] || {};
              const jobItem = itemMap[job.current_item_id] || itemMap[job.fg_item_id] || itemMap[job.raw_item_id] || null;
              // GIN-sourced entries have no production job — fall back to the QC record's own output_item snapshot.
              // A Job Work GIN always inspects its Received Part Number.
              const item = (rec.received_item_id && itemMap[rec.received_item_id]) || jobItem || itemMap[rec.output_item_id] || {
                code: rec.output_item_code || "",
                name: rec.output_item_name || "",
                uom: "",
              };
              const isFromGin = rec.source === "gin" || !!rec.gin_code;
              return (
                <tr key={rec.id} className="border-t border-slate-100" data-testid={`pending-row-${rec.job_code}`}>
                  <td className="py-2.5 px-4">
                    <div className="mono text-[11px] text-slate-500">{rec.job_code || "—"}</div>
                    {isFromGin ? (
                      <div className="text-slate-700 text-xs">
                        <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 text-[10px] uppercase tracking-wide mr-1">GIN</span>
                        <span className="mono">{rec.gin_code}</span>
                        {rec.dc_code && <div className="text-[11px] text-slate-500">Job Work DC <span className="mono">{rec.dc_code}</span>{rec.supplier ? ` · ${rec.supplier}` : ""}</div>}
                      </div>
                    ) : (
                      <div className="text-slate-700 text-xs">Batch <span className="mono">{job.batch_no || "—"}</span></div>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] uppercase tracking-wide">
                      {rec.stage_label || STAGE_LABEL[rec.stage]}
                    </span>
                  </td>
                  <td className="py-2.5 px-4">
                    <div className="mono text-xs text-slate-500">{item.code || "—"}</div>
                    <div className="text-slate-700 text-sm">{item.name || "—"}</div>
                    {rec.received_item_id && (
                      <div className="text-[10px] text-slate-500" data-testid={`pending-received-part-${rec.id}`}>
                        Received Part Number{rec.sent_item_code && rec.sent_item_code !== item.code ? ` · sent as ${rec.sent_item_code}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-right mono text-sm text-slate-800">{rec.pending_qty} {item.uom || ""}</td>
                  <td className="py-2.5 px-4 text-xs text-slate-700">
                    <ArrowRight className="inline w-3 h-3 mr-1" />
                    {rec.next_stage_label || STAGE_LABEL[rec.next_stage]}
                  </td>
                  <td className="py-2.5 px-4 text-xs text-slate-500">
                    {rec.created_at ? new Date(rec.created_at).toLocaleString() : "—"}<br />
                    <span className="text-slate-400">by {rec.requested_by || "—"}</span>
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    {canDecide ? (
                      <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-700" onClick={() => openInspection(rec)} data-testid={`inspect-btn-${rec.job_code}`}>
                        <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Inspect
                      </Button>
                    ) : (
                      <span className="text-[11px] text-slate-400 italic">Quality role required</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pending Goods Inward (GIN) Queue — incoming material awaiting accept/reject.
          This is a distinct workflow (see Goods Inward page) surfaced here so QC has
          one place to see everything awaiting their sign-off. */}
      <div className="bg-white border border-blue-200 rounded-lg overflow-hidden" data-testid="pending-gin-queue">
        <div className="px-4 py-3 border-b border-blue-100 text-sm font-semibold text-blue-800 flex items-center justify-between bg-blue-50">
          <span><Truck className="inline w-4 h-4 mr-1.5" /> Pending Goods Inward (GIN) Inspections</span>
          <span className="mono text-xs text-blue-700">{ginPending.length}</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
              <th className="py-2.5 px-4">GIN</th>
              <th className="py-2.5 px-4">Supplier</th>
              <th className="py-2.5 px-4">PO</th>
              <th className="py-2.5 px-4 text-right">Lines</th>
              <th className="py-2.5 px-4">Received</th>
              <th className="py-2.5 px-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {ginPending.length === 0 && (
              <tr>
                <td colSpan={6} className="py-10 text-center text-slate-500 text-sm">
                  <ShieldCheck className="inline w-4 h-4 mr-1 text-emerald-500" /> Queue clear — no goods inward pending inspection.
                </td>
              </tr>
            )}
            {ginPending.map((g) => (
              <tr key={g.id} className="border-t border-slate-100" data-testid={`pending-gin-row-${g.code}`}>
                <td className="py-2.5 px-4 mono text-xs text-slate-500">{g.code}</td>
                <td className="py-2.5 px-4 text-slate-700 text-sm">{g.supplier || "Manual receipt"}</td>
                <td className="py-2.5 px-4 mono text-xs text-blue-700">{g.po_code || "—"}</td>
                <td className="py-2.5 px-4 text-right mono text-sm text-slate-800">{g.lines?.length || 0}</td>
                <td className="py-2.5 px-4 text-xs text-slate-500">
                  {g.created_at ? new Date(g.created_at).toLocaleString() : "—"}<br />
                  <span className="text-slate-400">by {g.created_by || "—"}</span>
                </td>
                <td className="py-2.5 px-4 text-right">
                  {canDecide ? (
                    <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-700" onClick={() => navigate(`/goods-inward?gin=${g.id}`)} data-testid={`inspect-gin-btn-${g.code}`}>
                      <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Inspect
                    </Button>
                  ) : (
                    <span className="text-[11px] text-slate-400 italic">Quality role required</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Historic QC log */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="qc-history-log">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700">
            <ClipboardCheck className="inline w-4 h-4 mr-1.5 text-slate-500" />
            Inspection Log
            {stageFilter !== "all" && <span className="ml-2 text-xs font-normal text-slate-500">· {STAGE_LABEL[stageFilter]}</span>}
          </span>
          <span className="mono text-xs text-slate-500">{filteredLog.length}</span>
        </div>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500">
                <th className="py-2 px-3">Job</th>
                <th className="py-2 px-3">Stage</th>
                <th className="py-2 px-3">Inspector</th>
                <th className="py-2 px-3">Result</th>
                <th className="py-2 px-3 text-right">Passed</th>
                <th className="py-2 px-3 text-right">Failed</th>
                <th className="py-2 px-3">Date</th>
                <th className="py-2 px-3">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {filteredLog.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-sm text-slate-500">No inspections logged.</td></tr>
              )}
              {filteredLog.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`qc-log-row-${r.id}`}>
                  <td className="py-2 px-3 mono">{r.job_code}</td>
                  <td className="py-2 px-3">{STAGE_LABEL[r.stage] || r.stage}</td>
                  <td className="py-2 px-3">{r.inspector}</td>
                  <td className="py-2 px-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      (r.decision === "pass" || r.decision === "approved") ? "bg-emerald-50 text-emerald-700" :
                      r.decision === "partial" ? "bg-blue-50 text-blue-700" :
                      r.decision === "rework" ? "bg-orange-50 text-orange-700" : "bg-red-50 text-red-700"
                    }`}>{r.decision}</span>
                  </td>
                  <td className="py-2 px-3 text-right mono text-emerald-700">{r.passed_qty ?? "—"}</td>
                  <td className="py-2 px-3 text-right mono text-red-600">{r.failed_qty ?? "—"}</td>
                  <td className="py-2 px-3 text-xs text-slate-600">{r.decided_at ? new Date(r.decided_at).toLocaleString() : "—"}</td>
                  <td className="py-2 px-3 text-xs text-slate-600 max-w-md truncate">{r.notes || r.remarks || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Completion dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="qc-completion-dialog">
          <DialogHeader>
            <DialogTitle>
              Inspect {active?.job_code} — {active ? (active.stage_label || STAGE_LABEL[active.stage]) : ""}
            </DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs space-y-1">
                <div>Pending Quantity: <span className="mono font-semibold text-slate-800">{active.pending_qty}</span></div>
                <div>Next Stage on Pass: <span className="font-semibold text-slate-800">{active.next_stage_label || STAGE_LABEL[active.next_stage]}</span></div>
                <div className="text-slate-500">Requested {active.created_at ? new Date(active.created_at).toLocaleString() : "—"} by {active.requested_by}</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Quantity Passed *</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={passedQty}
                    onChange={(e) => setPassedQty(e.target.value)}
                    data-testid="qc-passed-qty-input"
                  />
                  <p className="text-[11px] text-emerald-700 mt-1">→ {STAGE_LABEL[active.next_stage] || active.next_stage}</p>
                </div>
                <div>
                  <Label>Quantity Failed</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={failedQty}
                    onChange={(e) => setFailedQty(e.target.value)}
                    data-testid="qc-failed-qty-input"
                  />
                  <p className="text-[11px] text-rose-700 mt-1">→ Rework Store</p>
                </div>
              </div>

              <div>
                <Label>Inspector Name *</Label>
                <Input value={inspector} onChange={(e) => setInspector(e.target.value)} data-testid="qc-inspector-input" />
              </div>
              <div>
                <Label>Remarks</Label>
                <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Observations, defects, corrective action…" data-testid="qc-remarks-input" />
              </div>

              {isTesting && (
                <div className="border border-slate-200 rounded-md p-3 space-y-2" data-testid="qc-checkpoints-section">
                  <div className="flex items-center justify-between">
                    <Label className="mb-0">Test Checkpoints</Label>
                    <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={addCheckpoint} data-testid="qc-add-checkpoint">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Checkpoint
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Any checkpoint marked out-of-spec (or a nonzero Failed qty) auto-raises a Non-Conformance with a 7-day closure date.
                  </p>
                  {checkpoints.length === 0 && <div className="text-xs text-slate-400 italic">No checkpoints entered.</div>}
                  {checkpoints.map((c, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`qc-checkpoint-row-${idx}`}>
                      <Input className="col-span-3 h-8 text-xs" placeholder="Name" value={c.name} onChange={(e) => setCheckpoint(idx, { name: e.target.value })} data-testid={`qc-checkpoint-name-${idx}`} />
                      <Input className="col-span-3 h-8 text-xs" placeholder="Expected" value={c.expected} onChange={(e) => setCheckpoint(idx, { expected: e.target.value })} />
                      <Input className="col-span-3 h-8 text-xs" placeholder="Actual" value={c.actual} onChange={(e) => setCheckpoint(idx, { actual: e.target.value })} />
                      <label className="col-span-2 flex items-center gap-1 text-xs">
                        <input type="checkbox" checked={c.passed} onChange={(e) => setCheckpoint(idx, { passed: e.target.checked })} data-testid={`qc-checkpoint-passed-${idx}`} />
                        In spec
                      </label>
                      <Button type="button" size="sm" variant="ghost" className="col-span-1 h-8 w-8 p-0 text-red-600" onClick={() => removeCheckpoint(idx)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                  {checkpoints.some((c) => !c.passed) && (
                    <div className="text-[11px] text-amber-700 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> Out-of-spec checkpoint(s) present — an NC will be auto-raised on submit.
                    </div>
                  )}
                </div>
              )}

              <div className="text-xs rounded p-2 bg-blue-50 border border-blue-200 text-blue-900">
                <strong>Passed + Failed = {(Number(passedQty || 0) + Number(failedQty || 0)).toFixed(2)}</strong> of {active.pending_qty} pending.
                {Number(passedQty || 0) > 0 && <> Passed qty auto-moves to <strong>{STAGE_LABEL[active.next_stage]}</strong>.</>}
                {Number(failedQty || 0) > 0 && <> Failed qty auto-moves to <strong>Rework Store</strong>.</>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submitInspection} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="qc-submit-inspection">
              Complete Inspection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
